import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Download,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Sliders,
} from 'lucide-react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import {
  quantityLabel,
  shouldShowLeads,
  isMultiStoreLeads,
  passbookCurrency,
} from '@/shared/lib/passbook-utils';
import { canAccessAudit } from '@/shared/lib/navigation';
import { cn } from '@/shared/lib/cn';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { SortSelect } from '@/shared/components/sort-select';
import { StoreFilterSelect } from '@/shared/components/store-filter-select';
import { PageSizeSelect } from '@/shared/components/page-size-select';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Tooltip } from '@/shared/ui/tooltip';
import { Pagination } from '@/shared/ui/pagination';
import { DatePicker } from '@/shared/ui/date-picker';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import type { Lead, Passbook, PanelUser } from '@/shared/types';
import { useLeadsFetcher } from '../api/use-leads';
import { useStores } from '../api/use-stores';
import {
  setLeadBlocked,
  blockAllSuspicious,
  useSuspiciousLeadsFetcher,
  type SuspiciousMeta,
} from '../api/use-audit';
import { LeadCard } from './lead-card';
import { LeadEditDialog } from './lead-edit-dialog';
import { ExportDialog } from './export-dialog';
import { AuditRulesDialog } from './audit-rules-dialog';

type AuditTab = 'suspeitos' | 'bloqueados';

const SORT_OPTIONS = [
  { value: 'updated:desc', label: 'Última atualização' },
  { value: 'updated:asc', label: 'Atualização: mais antigos' },
  { value: 'createdAt:desc', label: 'Cadastro: mais recentes' },
  { value: 'createdAt:asc', label: 'Cadastro: mais antigos' },
  { value: 'profile.name:asc', label: 'Nome A→Z' },
  { value: 'profile.name:desc', label: 'Nome Z→A' },
];

function parseSortOption(value: string) {
  const [id, dir] = value.split(':');
  return [{ id, desc: dir === 'desc' }];
}

function parseTab(value: string | null): AuditTab {
  return value === 'bloqueados' ? 'bloqueados' : 'suspeitos';
}

/** Mensagem que a API mandou (ex.: índice de auditoria ausente), não um genérico. */
function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message.trim() ? err.message : fallback;
}

/** "calculado agora / há 3 minutos" — o cálculo dos suspeitos é cacheado no backend. */
function formatComputedAt(iso: string): string {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return 'agora';
  const minutes = Math.floor((Date.now() - at) / 60_000);
  if (minutes <= 0) return 'agora';
  if (minutes === 1) return 'há 1 minuto';
  if (minutes < 60) return `há ${minutes} minutos`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? 'há 1 hora' : `há ${hours} horas`;
}

interface BlockReasonState {
  lead: Lead;
}

interface AuditLeadsListProps {
  tab: AuditTab;
  idPassbook: string;
  passbook: Passbook;
  canSeeEmail: boolean;
  scopeUser: PanelUser | null;
  onRequestBlock: (lead: Lead) => void;
  onUnblocked: () => void;
  refreshKey: number;
}

/** Corpo da listagem — reusa exatamente o stack de Leads (mesmo fetcher/card), só troca a opção do fetcher. */
function AuditLeadsList({
  tab,
  idPassbook,
  passbook,
  canSeeEmail,
  scopeUser,
  onRequestBlock,
  onUnblocked,
  refreshKey,
}: AuditLeadsListProps) {
  const confirm = useConfirm();
  const isSuspiciousTab = tab === 'suspeitos';
  const fetchBlocked = useLeadsFetcher(idPassbook, passbook, { onlyBlocked: true }, scopeUser);
  const fetchSuspicious = useSuspiciousLeadsFetcher(idPassbook, passbook, scopeUser);

  const [rows, setRows] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortValue, setSortValue] = useState('updated:desc');
  const [storeFilter, setStoreFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<SuspiciousMeta | null>(null);
  const [editLead, setEditLead] = useState<Lead | null>(null);
  const [listKey, setListKey] = useState(0);
  /** Consumido uma única vez pelo próximo fetch — força o backend a recalcular. */
  const forceRefresh = useRef(false);

  const labelQtde = quantityLabel(passbook);
  const isCashback = Boolean(passbook.hasCashback);
  const moneyCode = passbookCurrency(passbook);
  const hasMultiStore = isMultiStoreLeads(passbook);
  const { stores, resolveLeadStores } = useStores(idPassbook, hasMultiStore);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, idPassbook, sortValue, storeFilter, createdFrom, createdTo, pageSize]);

  useEffect(() => {
    let cancelled = false;
    const refresh = forceRefresh.current;
    forceRefresh.current = false;

    setIsLoading(true);
    setError(null);

    const params = {
      pageIndex,
      pageSize,
      sorting: parseSortOption(sortValue),
      search: debouncedSearch,
      storeFilter: storeFilter || undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    };

    const request: Promise<{ rows: Lead[]; total: number; meta?: SuspiciousMeta }> = isSuspiciousTab
      ? fetchSuspicious(params, { refresh })
      : fetchBlocked(params);

    request
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
        setTotal(result.total);
        setMeta(result.meta ?? null);
      })
      .catch((err: unknown) => {
        // Sem isto um 500 da API viraria "Nenhum lead suspeito" — foi assim que
        // a regra quebrada passou despercebida por semanas.
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setMeta(null);
        setError(errorMessage(err, 'Não foi possível carregar a lista.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    pageIndex,
    pageSize,
    debouncedSearch,
    sortValue,
    storeFilter,
    createdFrom,
    createdTo,
    isSuspiciousTab,
    fetchBlocked,
    fetchSuspicious,
    idPassbook,
    listKey,
    refreshKey,
  ]);

  /** Descarta o cache do backend e refaz a conta dos suspeitos. */
  const recompute = () => {
    forceRefresh.current = true;
    setListKey((k) => k + 1);
  };

  const handleToggleBlocked = async (lead: Lead) => {
    if (lead.status === 'blocked') {
      const ok = await confirm({
        title: 'Desbloquear lead',
        description: `Desbloquear "${lead.profile?.name || 'este lead'}"? Ele volta a acumular pontos normalmente.`,
        confirmLabel: 'Desbloquear',
      });
      if (!ok) return;
      try {
        await setLeadBlocked(idPassbook, lead._id, { blocked: false });
        toast.success('Lead desbloqueado.');
        setListKey((k) => k + 1);
        onUnblocked();
      } catch (err: unknown) {
        toast.error(errorMessage(err, 'Falha ao desbloquear.'));
      }
      return;
    }
    onRequestBlock(lead);
  };

  return (
    <>
      {error ? (
        <Card className="mb-4 flex flex-wrap items-start justify-between gap-3 border-destructive/40 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-medium">Falha ao carregar a lista</p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setListKey((k) => k + 1)}>
            Tentar de novo
          </Button>
        </Card>
      ) : null}

      {isSuspiciousTab && meta ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
          <span>
            ({meta.rule.maxTransactions} transações em {meta.rule.windowHours}h, últimos{' '}
            {meta.rule.lookbackDays} dias) · calculado {formatComputedAt(meta.computedAt)}
            {meta.truncated ? ' · lista limitada pelo teto de segurança' : ''}
          </span>
          <Button variant="ghost" size="sm" onClick={recompute} disabled={isLoading}>
            <RefreshCw className={cn('size-3.5', isLoading && 'animate-spin')} /> Recalcular
          </Button>
        </div>
      ) : null}

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome, CPF, e-mail…"
        filtersRow={
          <div
            className={cn(
              'grid gap-2',
              hasMultiStore
                ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5'
                : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
            )}
          >
            {hasMultiStore ? (
              <StoreFilterSelect
                value={storeFilter}
                stores={stores}
                onChange={setStoreFilter}
                className="w-full"
              />
            ) : null}
            <DatePicker
              value={createdFrom}
              onChange={setCreatedFrom}
              placeholder="Data início"
              className="w-full"
              toDate={createdTo ? new Date(`${createdTo}T23:59:59`) : undefined}
            />
            <DatePicker
              value={createdTo}
              onChange={setCreatedTo}
              placeholder="Data fim"
              className="w-full"
              fromDate={createdFrom ? new Date(`${createdFrom}T00:00:00`) : undefined}
            />
            <SortSelect
              value={sortValue}
              options={SORT_OPTIONS}
              onChange={setSortValue}
              className="w-full"
            />
            <PageSizeSelect value={pageSize} onChange={setPageSize} className="w-full" />
          </div>
        }
        selectAllId={`select-all-audit-${tab}`}
        itemCount={rows.length}
        allOnPageSelected={false}
        someOnPageSelected={false}
        onToggleAllOnPage={() => undefined}
        hideSelection
        isLoading={isLoading}
        isEmpty={!isLoading && !error && rows.length === 0}
        emptyTitle={tab === 'bloqueados' ? 'Nenhum lead bloqueado' : 'Nenhum lead suspeito'}
        emptyDescription={
          tab === 'bloqueados'
            ? 'Quando a auditoria bloquear cadastros, eles aparecerão aqui.'
            : 'Ninguém bateu nas regras de auditoria configuradas até agora.'
        }
        withFloatingBar={false}
        footer={
          total > 0 ? (
            <Pagination
              page={pageIndex}
              pageSize={pageSize}
              total={total}
              onPageChange={setPageIndex}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {rows.map((lead, index) => (
          <li key={lead._id}>
            <LeadCard
              lead={lead}
              index={index}
              selected={false}
              onToggle={() => undefined}
              onEdit={setEditLead}
              idPassbook={idPassbook}
              labelQtde={labelQtde}
              isCashback={isCashback}
              currencyCode={moneyCode}
              canSeeEmail={canSeeEmail}
              uniqueIdLabel={passbook.fieldUniqueIdLabel ?? 'CPF'}
              hasMultiStore={hasMultiStore}
              resolveLeadStores={hasMultiStore ? resolveLeadStores : undefined}
              hideSelection
              onToggleBlocked={handleToggleBlocked}
            />
          </li>
        ))}
      </SelectableListLayout>

      <LeadEditDialog
        lead={editLead}
        idPassbook={idPassbook}
        uniqueIdLabel={passbook.fieldUniqueIdLabel ?? 'CPF'}
        onClose={() => setEditLead(null)}
        onSaved={() => setListKey((k) => k + 1)}
      />
    </>
  );
}

/** Auditoria — regras configuráveis, leads suspeitos e bloqueados (antigo Antifraude). */
export function AuditPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));

  const [exportOpen, setExportOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState<BlockReasonState | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockingAll, setBlockingAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const canSeeEmail = user ? ['Client', 'Operator', 'Admin', 'Owner'].includes(user.role) : false;
  const canAccess = Boolean(
    user && shouldShowLeads(passbook, user.role) && canAccessAudit(user.email, passbook),
  );

  if (!canAccess) {
    return <Navigate to={`/loja/${idPassbook}/leads`} replace />;
  }

  const setTab = (next: AuditTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'suspeitos') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const confirmBlock = async () => {
    if (!blockTarget) return;
    try {
      await setLeadBlocked(idPassbook, blockTarget.lead._id, {
        blocked: true,
        reason: blockReason.trim() || 'Bloqueio manual',
        ruleType: 'manual',
      });
      toast.success('Lead bloqueado.');
      setBlockTarget(null);
      setBlockReason('');
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      toast.error(errorMessage(err, 'Falha ao bloquear.'));
    }
  };

  const handleBlockAllSuspicious = async () => {
    setBlockingAll(true);
    try {
      const { blocked } = await blockAllSuspicious(idPassbook);
      if (blocked > 0) {
        toast.success(
          `${blocked} lead${blocked > 1 ? 's' : ''} bloqueado${blocked > 1 ? 's' : ''}.`,
        );
        setRefreshKey((k) => k + 1);
      } else {
        toast.info('Nenhum lead suspeito pra bloquear agora.');
      }
    } catch (err: unknown) {
      toast.error(errorMessage(err, 'Falha ao bloquear os suspeitos.'));
    } finally {
      setBlockingAll(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Auditoria"
        description="Regras de auditoria"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Tooltip content="Configurar as regras que marcam um lead como suspeito" side="bottom">
              <Button variant="secondary" onClick={() => setRulesOpen(true)}>
                <Sliders className="size-4" /> Configurar regras
              </Button>
            </Tooltip>
            <Tooltip content="Exportar base ou logs bloqueados por e-mail" side="bottom">
              <Button variant="secondary" onClick={() => setExportOpen(true)}>
                <Download className="size-4" /> Exportar
              </Button>
            </Tooltip>
            {tab === 'suspeitos' ? (
              <Tooltip content="Bloquear de uma vez todos os leads suspeitos hoje" side="bottom">
                <Button
                  variant="destructive"
                  onClick={handleBlockAllSuspicious}
                  loading={blockingAll}
                >
                  <ShieldX className="size-4" /> Bloquear todos os suspeitos
                </Button>
              </Tooltip>
            ) : null}
          </div>
        }
      />

      <div className="mb-6 flex items-start justify-between gap-3 rounded-2xl border border-border/60 bg-muted/30 px-4 py-3">
        <div className="flex items-start gap-3 text-sm text-muted-foreground">
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" />
          <p>
            {tab === 'suspeitos'
              ? 'Leads que bateram nas regras de auditoria mas ainda não foram bloqueados.'
              : 'Leads bloqueados manualmente ou pela auditoria automática.'}
          </p>
        </div>
        <SegmentedControl
          value={tab}
          onChange={setTab}
          options={[
            { value: 'suspeitos', label: 'Suspeitos', icon: ShieldAlert },
            { value: 'bloqueados', label: 'Bloqueados', icon: ShieldCheck },
          ]}
          size="sm"
        />
      </div>

      <AuditLeadsList
        key={tab}
        tab={tab}
        idPassbook={idPassbook}
        passbook={passbook}
        canSeeEmail={canSeeEmail}
        scopeUser={user}
        onRequestBlock={(lead) => setBlockTarget({ lead })}
        onUnblocked={() => setRefreshKey((k) => k + 1)}
        refreshKey={refreshKey}
      />

      <ExportDialog
        idPassbook={idPassbook}
        open={exportOpen}
        onOpenChange={setExportOpen}
        blocked
      />

      <AuditRulesDialog
        idPassbook={idPassbook}
        open={rulesOpen}
        onOpenChange={setRulesOpen}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />

      <Dialog
        open={Boolean(blockTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setBlockTarget(null);
            setBlockReason('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bloquear {blockTarget?.lead.profile?.name || 'lead'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="block-reason">Motivo / evidência</Label>
            <Textarea
              id="block-reason"
              rows={3}
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Ex.: várias transações em minutos, sem histórico de compra…"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setBlockTarget(null);
                setBlockReason('');
              }}
            >
              Cancelar
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmBlock()}>
              Bloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
